import * as React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { Box, Grid, Link, Typography } from "@material-ui/core";
import KeyboardArrowLeftOutlinedIcon from "@material-ui/icons/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@material-ui/icons/KeyboardArrowRightOutlined";

import { Page } from "../Page";
import { IPageProps } from "../IPageProps";

import { QuiltBotRoute } from "Routes";

import { QuiltThumbnail } from "Components/QuiltThumbnail";
import { RequestProgress } from "Components/RequestProgress";

import { Status, useHttpGet } from "Utility/useHttp";

import { IFavoriteQuilt } from "Model/IFavoriteQuilt";
import { IQuiltBotTweetsResponse } from "Model/IQuiltBotTweetsResponse";

function getDateString(year: number, month: number): string {
    const date = new Date(year, month - 1, 1);

    return date.toLocaleString("default", { month: "long", year: "numeric" });
}

function sortQuilts(quilts: IFavoriteQuilt[]): IFavoriteQuilt[] {
    const res = [...quilts];

    res.sort((a, b) => b.addedDateUtc.localeCompare(a.addedDateUtc));

    return res;
}

function renderPrevious(response: IQuiltBotTweetsResponse): React.ReactElement | null {
    if (!response.previous) {
        return null;
    }

    const url = QuiltBotRoute.path + `?year=${response.previous.year}&month=${response.previous.month}`;

    return (
        <Link key="prev" underline="none" component={RouterLink} to={url}>
            <Box display="flex" flexDirection="row">
                <KeyboardArrowLeftOutlinedIcon />
                <Typography>{getDateString(response.previous.year, response.previous.month)}</Typography>
            </Box>
        </Link>
    )
}

function renderNext(response: IQuiltBotTweetsResponse): React.ReactElement | null {
    if (!response.next) {
        return null;
    }

    const url = QuiltBotRoute.path + `?year=${response.next.year}&month=${response.next.month}`;

    return (
        <Link key="next" underline="none" component={RouterLink} to={url}>
            <Box display="flex" flexDirection="row">
                <Typography>{getDateString(response.next.year, response.next.month)}</Typography>
                <KeyboardArrowRightOutlinedIcon />
            </Box>
        </Link>
    );
}

function renderHeader(response: IQuiltBotTweetsResponse): React.ReactElement | null {
    if (response.previous === null && response.next === null) {
        return null;
    }
 
    return (
        <Grid container spacing={1} direction="row" justify="center">
            <Grid item>
                {renderPrevious(response)}
            </Grid>
            <Grid item>
                <Typography>{getDateString(response.year, response.month)}</Typography>
            </Grid>
            <Grid item>
                {renderNext(response)}
            </Grid>
        </Grid>
    );
}

function renderQuiltOfTheDay(response: IQuiltBotTweetsResponse | null): React.ReactElement | null {
    if (response === null) {
        return null;
    }

    const quilts = sortQuilts(response.quilts);

    const items = quilts.map((q, index) => {
        const date = new Date(q.addedDateUtc);

        const month = date.getMonth() + 1;
        const paddedMonth = month.toString().padStart(2, '0');
        const paddedDay = date.getDate().toString().padStart(2, '0');

        const dateStr = `${paddedMonth}/${paddedDay}/${date.getFullYear()}`;
        const thumbnailUrl = `quilt-bot/${q.id}.png`;

        return (
            <Box p={1} key={index} display="flex" flexDirection="column" alignItems="center">
                <QuiltThumbnail code={q.code} thumbnailUrl={thumbnailUrl} />
                <Typography>{dateStr}</Typography>
            </Box>
        )
    });

    if (items.length === 0) {
        items.push(<Typography key="none">No quilts generated yet!</Typography>);
    };

    return (
        <>
            <Box mb={2}>
                <Typography variant="h5">Quilt of the Day</Typography>
            </Box>

            {renderHeader(response)}

            <Box display="flex" flexDirection="row" justifyContent="center" flexWrap="wrap">
                {items}
            </Box>
        </>
    )
}

function getBotUrl(year: number, month: number): string {
    return `api/quiltbot?year=${year}&month=${month}`;
}

export function QuiltBotPage(props: IPageProps): React.ReactElement {
    const [url, setUrl] = React.useState<string | null>(null);
    const request = useHttpGet<IQuiltBotTweetsResponse>(url);

    const location = useLocation();

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const year = parseInt(params.get("year") ?? "", 10);
        const month = parseInt(params.get("month") ?? "", 10);

        if (!isNaN(year) && !isNaN(month)) {
            setUrl(getBotUrl(year, month));
        } else {
            const now = new Date();
            setUrl(getBotUrl(now.getFullYear(), now.getMonth() + 1));
        }
    },
    [location])

    const response = request.status === Status.Complete ? request.data : null;

    return (
        <Page>
            <RequestProgress request={request} errorMessage="Failed to load quilt bot quilts." />
            {renderQuiltOfTheDay(response)}
        </Page>
    );
}